// LimitedPartnerPortal.jsx
import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import {
  collection, query, where, getDocs, doc, setDoc,
  orderBy, getDoc, Timestamp
} from "firebase/firestore";
import {
  onAuthStateChanged, signInWithPopup, GoogleAuthProvider, signOut,
  isSignInWithEmailLink, signInWithEmailLink
} from "firebase/auth";
import { db, auth, functions } from "../../firebaseConfig.js";
import { httpsCallable } from "firebase/functions";
import Papa from "papaparse";
import { saveAs } from "file-saver";
import { BadgeNotification, TrophyCase } from "../badges/BadgeDisplay";
import { getChapterMembershipLinks } from "../../data/chapterConfig";
import PitchMap from "../pitch/PitchMap";
import {
  trackReviewSubmission,
  initializeUserStats,
  calculateRetroactiveStats,
  getCurrentQuarter,
} from "../../services/statsTracking";
import OSIcon32 from "../icons/OSIcon32";
import {
  RetroButton,
  ConfirmDialog,
  AdminTabStrip,
  useConfirm,
} from "../ui/retro";
import {
  roleLabel,
  roleTonePill,
} from "../../helpers/labels";
import {
  sumAwarded,
  chaptersByNameMap,
} from "../../helpers/awardAmount";
import { isManualAward } from "../../helpers/manualAward";
import { DESKTOP_LAUNCH_KEY } from "../../data/desktopPrograms";
import ResourceLibrary from "./resources/ResourceLibrary";
import FormsLibrary from "./forms-library/FormsLibrary";
import AssetsLibrary from "./assets-library/AssetsLibrary";
import StatisticsTab from "./statistics/StatisticsTab";
import "./review/live-review.css";
import { ToastContainer, notify } from "../ui/Toast";
import SocialCardsTab from "./tabs/SocialCardsTab";
import ChapterMembersTab from "./tabs/ChapterMembersTab";
import BulletinBoardTab from "./tabs/BulletinBoardTab";
import ReviewPitchesTab from "./tabs/ReviewPitchesTab";
import GrantWinnersAdminTab from "./tabs/admin/GrantWinnersAdminTab";
import FormResponsesAdminTab from "./tabs/admin/FormResponsesAdminTab";
import UsersAdminTab from "./tabs/admin/UsersAdminTab";
import CreateUserAdminTab from "./tabs/admin/CreateUserAdminTab";
import ChapterAdminTab from "./tabs/admin/ChapterAdminTab";
import SuperAdminToolsTab from "./tabs/admin/SuperAdminToolsTab";
import ResourcesAdminTab from "./tabs/admin/ResourcesAdminTab";
import ReviewsAdminTab from "./tabs/admin/ReviewsAdminTab";
import { VALID_ROLES } from "../../data/roles";

// --- Constants ---
const provider = new GoogleAuthProvider();
const APP_NAME = "Neighborhood OS";
const CHAPTERS = ['Western New York', 'Central New York', 'Capital Region', 'Hudson Valley', 'Denver'];
const sendSignInLinkCallable = httpsCallable(functions, "sendSignInLink");
const claimPendingProfileCallable = httpsCallable(functions, "claimPendingProfile");
const EMAIL_FOR_SIGN_IN_KEY = "lpPortal:emailForSignIn";


// --- Helper Functions for Alerts/Confirmations ---
const showAppAlert = (message, options) => {
  notify(message, options);
};

const showAppConfirm = (message) => {
  return window.confirm(message); 
};


// --- Helper Components ---
function AuthInput({ type = "text", placeholder, value, onChange, required, minLength, ariaLabel }) {
  const autoCompleteType = type === 'email' ? 'email' : type === 'password' ? (placeholder.toLowerCase().includes('new') ? 'new-password' : 'current-password') : 'off';
  const inputMode = type === 'email' ? 'email' : undefined;
  return (
     <input
       type={type}
       placeholder={placeholder}
       value={value}
       onChange={onChange}
       required={required}
       minLength={minLength}
       aria-label={ariaLabel || placeholder}
       inputMode={inputMode}
       className="auth-input"
       style={{ display: 'block', width: 'calc(100% - 20px)', padding: '10px', margin: '10px auto', border: '2px solid', borderColor: 'var(--pink-dark) var(--bevel-highlight) var(--bevel-highlight) var(--pink-dark)', boxShadow: 'var(--shadow-inset)', boxSizing: 'border-box', background: 'var(--gnf-bg)', fontFamily: 'inherit', fontSize: '1em' }}
       autoComplete={autoCompleteType}
     />
  );
}

// RetroButton previously defined inline here; moved to a shared primitive so
// hover/active/focus states are consistent and a11y-friendly across the app.
// See src/components/ui/retro/RetroButton.jsx — imported at the top of this file.



// --- Main Component ---
export default function LimitedPartnerPortal({ onOpenGNFWebsite, isStandalone = false }) {

// Retro-styled confirm dialog (replaces window.confirm for the main destructive
// admin actions — delete user, delete chapter, delete resource). Other call sites
// can migrate over time using the same `requestConfirm` helper.
const { requestConfirm, confirmDialog } = useConfirm();

// --- State Variables ---
const [user, setUser] = useState(null);
const [isLoadingAuth, setIsLoadingAuth] = useState(true);
const [email, setEmail] = useState("");
const [authError, setAuthError] = useState("");
const [signInLinkSending, setSignInLinkSending] = useState(false);
const [signInLinkSentTo, setSignInLinkSentTo] = useState("");
const [completingSignIn, setCompletingSignIn] = useState(false);
const [activeTab, setActiveTab] = useState("reviewPitches");
const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
const [activeAdminTab, setActiveAdminTab] = useState("pitchesAndReviews");

// Reactive phone-width flag — a matchMedia subscription, so rotating or
// resizing re-renders consumers (the admin reviews tab coerces its Live
// mode to List off this). Kept callable so existing isMobile() sites work.
const [isMobileViewport, setIsMobileViewport] = useState(
  () => window.matchMedia("(max-width: 767px)").matches
);
useEffect(() => {
  const mq = window.matchMedia("(max-width: 767px)");
  const onChange = (e) => setIsMobileViewport(e.matches);
  mq.addEventListener("change", onChange);
  return () => mq.removeEventListener("change", onChange);
}, []);
const isMobile = () => isMobileViewport;
const [expandedPitchId, setExpandedPitchId] = useState(null);
const [lpPitches, setLpPitches] = useState([]);
const [lpPitchesLoaded, setLpPitchesLoaded] = useState(false);
const [selectedPitch, setSelectedPitch] = useState(null);
const [reviews, setReviews] = useState({}); // State for user's reviews { pitchId: reviewData }
const [reviewFormData, setReviewFormData] = useState({});
// Submit-button feedback: idle → saving (write in flight) → saved (success
// stamp, held briefly before the panel closes). Guards double-submits too.
const [reviewSubmitPhase, setReviewSubmitPhase] = useState('idle');
// Whether the in-flight submit is an edit — captured at submit time so the
// button label doesn't flip to "Updated" mid-flight when setReviews lands.
const [reviewSubmitWasEdit, setReviewSubmitWasEdit] = useState(false);
const reviewSuccessTimerRef = useRef(null);
const [hidePassedReviews, setHidePassedReviews] = useState(false);
const [reviewSearchTerm, setReviewSearchTerm] = useState("");
const [reviewFilter, setReviewFilter] = useState("all");
const [reviewChapterFilter, setReviewChapterFilter] = useState("");
const [reviewQuarterFilter, setReviewQuarterFilter] = useState([]); // Multi-select quarter filter for review pitches
const [reviewCategoryFilter, setReviewCategoryFilter] = useState(""); // AI-generated category filter for review pitches
const [adminPitches, setAdminPitches] = useState([]);
const [users, setUsers] = useState([]);
const [chapterMembers, setChapterMembers] = useState([]);
const [allReviewsData, setAllReviewsData] = useState([]); // State for ALL reviews [{...reviewData}]
const [pitchComments, setPitchComments] = useState({}); // { pitchId: [{ comment, reviewerName, reviewerId }] }
const [adminChapterFilter, setAdminChapterFilter] = useState("");
const [adminQuarterFilter, setAdminQuarterFilter] = useState([]);
const [adminSearch, setAdminSearch] = useState("");
const [adminHidePassed, setAdminHidePassed] = useState(false);
const [adminFavoriteFilterMode, setAdminFavoriteFilterMode] = useState("all"); // all | favsOnly | favsAndCons | shortlisted
const [adminSortMode, setAdminSortMode] = useState("newest"); // newest | oldest | avgDesc | avgAsc | sumDesc | sumAsc | mostReviews
// Admin discussion notes. Flat list, grouped by pitchId at render. Loaded
// once with the rest of the admin data and refreshed after every create/edit/delete.
const [adminNotes, setAdminNotes] = useState([]);
// Draft composer state keyed by pitchId so each expanded card keeps its own draft.
const [adminNoteDrafts, setAdminNoteDrafts] = useState({});
// Edit state — which note is being edited, and the in-flight text for that edit.
const [editingAdminNoteId, setEditingAdminNoteId] = useState(null);
const [editingAdminNoteText, setEditingAdminNoteText] = useState("");
const [adminNoteSavingId, setAdminNoteSavingId] = useState(null);
const [shortlistTogglingId, setShortlistTogglingId] = useState(null);
// Live Review focus: which pitch the right pane is showing. Distinct from
// expandedPitchId, which remains List-mode-only.
const [focusedPitchId, setFocusedPitchId] = useState(null);
const [detailsDrawerOpen, setDetailsDrawerOpen] = useState(false);
const [aboutById, setAboutById] = useState({});
const [websiteById, setWebsiteById] = useState({});
const [winnerChapterFilter, setWinnerChapterFilter] = useState("");
const [photosById, setPhotosById] = useState({});
const [winnerSearchTerm, setWinnerSearchTerm] = useState("");
const [pendingChanges, setPendingChanges] = useState({});
const [generatingAboutId, setGeneratingAboutId] = useState(null);

// Gamification state
const [userStats, setUserStats] = useState(null);
const [userBadges, setUserBadges] = useState([]);
const [showBadgeNotification, setShowBadgeNotification] = useState(null);
const [statsInitialized, setStatsInitialized] = useState(false);

// User creation state
const [newUserData, setNewUserData] = useState({
  email: '',
  name: '',
  role: 'lp',
  chapter: '',
  anniversary: new Date().toISOString().split('T')[0]
});
const [isInviting, setIsInviting] = useState(false);

// User editing state
const [editingUsers, setEditingUsers] = useState({}); // { userId: { linkedinUrl: '', professionalRole: '', bio: '', newEmail: '' } }
// Per-user member-photo upload progress, keyed by uid → boolean.
const [uploadingPhotoFor, setUploadingPhotoFor] = useState({});
const [updatingEmailFor, setUpdatingEmailFor] = useState({});

// Chapters collection state. Seeded from the hardcoded CHAPTERS constant until the
// /chapters collection is populated. `chapters` holds raw docs; `chapterNames` is
// what dropdowns render — always the source-of-truth list of display names.
const [chapters, setChapters] = useState([]);
const [chaptersLoaded, setChaptersLoaded] = useState(false);
const [editingChapterId, setEditingChapterId] = useState(null);
const [chapterFormData, setChapterFormData] = useState({
  slug: '',
  name: '',
  pageSlug: '',
  tagline: '',
  foundedYear: '',
  foundedDate: '',
  foundingWindowDays: 365,
  emailAlias: '',
  slackChannel: '',
  lpSlackChannel: '',
  active: true,
  order: 0,
  // Landing page content (hydrated onto public/<slug>.html by chapter-hydration.js)
  heroTitle: '',
  heroTagline: '',
  heroImage: '',
  heroImageCaption: '',
  servingTitle: '',
  servingText: '',
  counties: [],
  poweredByText: '',
  galleryPhotos: [],
  showLPs: true,
  showGallery: true,
  showImpact: true
});
const [isAddingChapter, setIsAddingChapter] = useState(false);
const [showNewChapterWizard, setShowNewChapterWizard] = useState(false);
// Active tab inside the chapter edit panel. Values: 'identity' | 'content' | 'photos' | 'visibility'.
const [chapterFormTab, setChapterFormTab] = useState('content');

// Message board state
const [bulletinMessages, setBulletinMessages] = useState([]);
const [newBulletinMessage, setNewBulletinMessage] = useState('');
const [lastViewedTimestamp, setLastViewedTimestamp] = useState(() => {
  const stored = localStorage.getItem(`messageBoard_lastViewed_${user?.uid}`);
  return stored || new Date().toISOString();
});

// Resources management state
const [managedResources, setManagedResources] = useState([]);
const [editingResource, setEditingResource] = useState(null);
const [resourceFormData, setResourceFormData] = useState({
  Resource: '',
  Chapter: '',
  Type: '',
  'Focus Area': '',
  'Business Stage': 'Ideation',
  'Counties Served': '',
  URL: '',
  'Expanded Details': '',
  'Average Check Size': '',
  'Relocation Required?': 'No'
});
const [isAddingResource, setIsAddingResource] = useState(false);
const [resourceSearchTerm, setResourceSearchTerm] = useState('');
const [resourceTypeFilter, setResourceTypeFilter] = useState('');
const [resourceStageFilter, setResourceStageFilter] = useState('');
const [resourceChapterFilter, setResourceChapterFilter] = useState('');
// CSV bulk-import preview state. Status walks: idle → preview → committing → idle.
const [resourceCsvImport, setResourceCsvImport] = useState({ valid: [], errors: [], status: 'idle' });
const resourceCsvFileInputRef = useRef(null);

// Calculate new message count
const newMessageCount = useMemo(() => {
  if (!lastViewedTimestamp || !bulletinMessages.length) return 0;
  const lastViewed = new Date(lastViewedTimestamp);
  return bulletinMessages.filter(msg => {
    const msgDate = msg.timestamp?.toDate ? msg.timestamp.toDate() : new Date(msg.timestamp);
    return msgDate > lastViewed && msg.authorId !== user?.uid;
  }).length;
}, [bulletinMessages, lastViewedTimestamp, user?.uid]);

// --- Refs ---
const listScrollRef = useRef(null);
const [listScrollPosition, setListScrollPosition] = useState(0);

// Scroll the main content area back to the top whenever the user navigates
// to a new tab (or admin sub-tab). Without this, switching from a deep
// scroll position into a fresh tab leaves you mid-page in the new content.
useEffect(() => {
  if (listScrollRef.current) {
    listScrollRef.current.scrollTop = 0;
  }
}, [activeTab, activeAdminTab]);

// --- Derived State (Roles) ---
const isSuperAdmin = user?.role === "superAdmin";
const isChapterDirector = user?.role === "chapter_director";
const isLP = user?.role === "lp";
// isAdmin gates the Admin Panel and admin-only actions (shortlist, admin notes,
// user/chapter management). Plain LPs review pitches but do not run the meeting,
// so they are excluded.
const isAdmin = isChapterDirector || isSuperAdmin;
const userChapter = user?.chapter;

// Chapter directors can only invite into their own chapter — keep it pinned
// even if the field was blank on first mount.
useEffect(() => {
  if (isChapterDirector && userChapter && newUserData.chapter !== userChapter) {
    setNewUserData((prev) => ({ ...prev, chapter: userChapter }));
  }
}, [isChapterDirector, userChapter, newUserData.chapter]);

// Active chapter names, sourced dynamically from /chapters when populated. Falls
// back to the hardcoded CHAPTERS constant while the collection is empty or still
// loading so dropdowns never collapse to nothing.
const activeChapterNames = useMemo(() => {
  const fromCollection = chapters
    .filter(c => c.active !== false)
    .map(c => c.name)
    .filter(Boolean);
  return fromCollection.length > 0 ? fromCollection : CHAPTERS;
}, [chapters]);

// --- Helper Function: Calculate Quarter ---
const getQuarterFromDate = (dateValue) => {
    try {
        let date;
        if (typeof dateValue?.toDate === 'function') {
            date = dateValue.toDate();
        } else if (dateValue instanceof Date) {
            date = dateValue;
        } else {
            date = new Date(dateValue);
        }
        if (date instanceof Date && !isNaN(date)) {
            return `Q${Math.floor(date.getMonth() / 3) + 1} ${date.getFullYear()}`;
        }
    } catch (e) { /* ignore errors */ }
    return "Invalid Quarter";
};


// --- Effects ---

// Effect 0: Magic-link return-trip handler. Runs once on mount BEFORE the
// auth listener registers a user. If the current URL is a Firebase
// email-link, complete the sign-in with the email we stashed in
// localStorage when the user requested the link. We then strip the link
// query params from the URL so a refresh doesn't try to re-consume the
// (now-spent) oobCode.
useEffect(() => {
  if (typeof window === "undefined") return;
  if (!isSignInWithEmailLink(auth, window.location.href)) return;

  const completeFromLink = async () => {
    setCompletingSignIn(true);
    let storedEmail = window.localStorage.getItem(EMAIL_FOR_SIGN_IN_KEY) || "";
    if (!storedEmail) {
      // Cross-device case: link opened on a device that never requested it.
      // Prompt for the original address so Firebase can verify the link.
      storedEmail = window.prompt(
        "To finish signing in, confirm the email address you requested the sign-in link with:"
      ) || "";
      storedEmail = storedEmail.trim().toLowerCase();
    }
    if (!storedEmail) {
      setCompletingSignIn(false);
      setAuthError("Sign-in cancelled — no email confirmed.");
      return;
    }
    try {
      await signInWithEmailLink(auth, storedEmail, window.location.href);
      window.localStorage.removeItem(EMAIL_FOR_SIGN_IN_KEY);
      // Clean the URL so a refresh doesn't try to re-consume the spent oobCode.
      window.history.replaceState({}, document.title, window.location.pathname);
    } catch (err) {
      console.error("LPPortal: signInWithEmailLink failed:", err);
      const friendly =
        err?.code === "auth/invalid-action-code"
          ? "This sign-in link has already been used or expired. Request a new one below."
          : err?.code === "auth/invalid-email"
          ? "That email doesn't match the address the link was sent to."
          : err?.message || "Sign-in failed.";
      setAuthError(friendly);
    } finally {
      setCompletingSignIn(false);
    }
  };

  completeFromLink();
  // Intentionally no deps — this should run exactly once on mount.
}, []);

// Effect 1: Authentication Listener
useEffect(() => {
  setIsLoadingAuth(true);
  const unsubscribe = onAuthStateChanged(auth, async (authUserData) => {
    if (authUserData) {
      try {
        const userDocRef = doc(db, "users", authUserData.uid);
        const userDocSnap = await getDoc(userDocRef);
        if (userDocSnap.exists()) {
          const userData = userDocSnap.data();
          if (userData.role && VALID_ROLES.includes(userData.role)) {
            setUser({ uid: authUserData.uid, email: authUserData.email, ...userData });
            setAuthError("");
            
            // Initialize stats and badges
            setUserStats(userData.stats || {
              totalReviews: 0,
              quarterReviews: 0,
              favoritesPicked: 0,
              considerationsPicked: 0,
              passedPicked: 0,
              ineligiblePicked: 0,
              reviewsByQuarter: {},
              reviewsByHour: {},
              longestStreak: 0,
              currentStreak: 0,
              lastReviewDate: null,
              averageReviewLength: 0,
              totalComments: 0,
              winnersIdentified: 0,
              accuracyRate: 0
            });
            const userBadgesData = userData.badges || [];
            setUserBadges(userBadgesData);
            
            // If the user arrived here from a gated /pitch/:id link, bounce
            // them back there instead of the default portal landing.
            const pendingPitchUrl = sessionStorage.getItem('pendingPitchUrl');
            if (pendingPitchUrl) {
              sessionStorage.removeItem('pendingPitchUrl');
              window.location.href = pendingPitchUrl;
              return;
            }

            // If not in standalone mode and user just logged in, redirect to portal
            if (!isStandalone && !sessionStorage.getItem('lpPortalRedirected')) {
              sessionStorage.setItem('lpPortalRedirected', 'true');
              window.location.href = '/portal';
            }
            
          } else {
            console.warn("LPPortal: User document found but role invalid or missing:", userData.role);
            setUser(null);
            setAuthError("Account role invalid or missing. Please contact an administrator."); 
            await signOut(auth);
          }
        } else {

          // Claim any legacy pending profile SERVER-SIDE. The client is no
          // longer allowed to create its own /users doc (that was the
          // privilege-escalation hole — an attacker could self-assign
          // role:'superAdmin'). The claimPendingProfile Cloud Function migrates
          // an admin-created pending profile to this uid with the admin SDK, so
          // the role can only come from the pending doc, never from the client.
          try {
            const claimResult = await claimPendingProfileCallable();
            if (claimResult?.data?.claimed) {
              // The auth state listener will re-trigger and find the proper document.
              return;
            }
          } catch (claimErr) {
            console.error("LPPortal: claimPendingProfile failed:", claimErr);
          }

          console.warn("LPPortal: User exists in Auth but not Firestore. Account setup may be incomplete.");
          setUser(null);
          setAuthError("Your account setup is incomplete. Please contact your administrator.");
          await signOut(auth);
        }
      } catch (error) {
        console.error("LPPortal: Error during auth state change processing:", error);
        setUser(null);
        setAuthError(`Error verifying account details: ${error.message}`);
        try { await signOut(auth); } catch (signOutError) { console.error("LPPortal: Error signing out after auth verification failure:", signOutError); }
      }
    } else {
      // User is logged out
      setUser(null);
      setAuthError("");
      setLpPitches([]);
      setLpPitchesLoaded(false);
      setAdminPitches([]);
      setReviews({});
      setSelectedPitch(null);
      setReviewFormData({});
      setUsers([]);
      setAllReviewsData([]);
      setAdminNotes([]);
      setChapterMembers([]);
      setActiveTab("reviewPitches");
      setActiveAdminTab("userManagement");
    }
    setIsLoadingAuth(false);
  });
  return () => {
    unsubscribe();
  };
}, []);

// Effect 2: Initialize stats if needed
useEffect(() => {
  const initStats = async () => {
    if (user && !statsInitialized) {
      if (!userStats) {
        const stats = await initializeUserStats(user.uid);
        setUserStats(stats);
        
        // Calculate retroactive stats if this is first time
        if (stats && stats.totalReviews === 0) {
          const retroData = await calculateRetroactiveStats(user.uid);
          if (retroData) {
            setUserStats(retroData.stats);
            setUserBadges(retroData.badges);
          }
        }
      }
      setStatsInitialized(true);
    }
  };
  
  initStats();
}, [user, userStats, statsInitialized]);

// --- Data Loading Functions 
const loadLPData = useCallback(async () => {
  if (!user || !user.uid) {
    return;
  }
  const chapterToQuery = isSuperAdmin ? null : userChapter;
  // Allow SuperAdmins to load even without a chapter, but normal LPs/Admins need one
  if (!chapterToQuery && !isSuperAdmin) {
    console.warn(`LPPortal: Skipping LP data load: No chapter assigned for non-SuperAdmin user ${user.email}`);
    setLpPitches([]);
    setLpPitchesLoaded(true);
    setReviews({});
    return;
  }

  let pitchDocs = [];

  // Load Pitches
  try {
    let pitchQuery = chapterToQuery
      ? query(collection(db, "pitches"), where("chapter", "==", chapterToQuery), orderBy("createdAt", "desc"))
      : query(collection(db, "pitches"), orderBy("createdAt", "desc")); // SuperAdmin gets all

    const pitchSnap = await getDocs(pitchQuery);
    pitchDocs = pitchSnap.docs.map(d => {
      const data = d.data();
      // Ensure createdAt is handled correctly, default to null if invalid
      const createdDateValue = data.createdAt; // Could be Timestamp, Date, string, null etc.
      const quarter = getQuarterFromDate(createdDateValue);
      return {
        id: d.id,
        ...data,
        // Store the raw value; formatting happens in formatDate
        createdAt: createdDateValue,
        // Keep createdDate for backward compatibility if needed? Or rely solely on formatDate(createdAt)
        createdDate: createdDateValue,
        quarter: quarter
      };
    });
    setLpPitches(pitchDocs);
    setLpPitchesLoaded(true);
  } catch (error) {
    console.error(`LPPortal: Error loading LP pitches for ${user.email} / chapter ${chapterToQuery}:`, error.code, error.message);
    setLpPitches([]);
    setLpPitchesLoaded(true);
    setReviews({});
    showAppAlert(`Error loading pitches: ${error.message}. Please check console for details. You may need to refresh or contact support if this persists.`);
    return;
  }

  // Load Reviews for this user via a single reviewerId query. Earlier this
  // fanned out one getDoc per pitch (N+1 reads); the where() query below
  // covers the same ground in one round-trip and stays cheap as the chapter
  // grows. Reviews always carry both reviewerId and pitchId on write, so the
  // doc-ID fallback is no longer load-bearing.
  try {
    const reviewQuery = query(collection(db, "reviews"), where("reviewerId", "==", user.uid));
    const reviewSnap = await getDocs(reviewQuery);
    const reviewsMap = {};
    reviewSnap.forEach((docSnap) => {
      const reviewData = docSnap.data();
      if (reviewData.pitchId) {
        reviewsMap[reviewData.pitchId] = { reviewId: docSnap.id, ...reviewData };
      } else {
        console.warn("LPPortal: Found review without pitchId, skipping:", docSnap.id);
      }
    });
    setReviews(reviewsMap);
  } catch (reviewError) {
    console.error(`LPPortal: Error loading reviews for user ${user.email}:`, reviewError.code, reviewError.message);
    setReviews({}); // Clear reviews on error, but don't clear pitches
    // Don't alert again, pitch error is more critical
  }

  // Load all review comments for pitches in this chapter (for shared team notes)
  try {
    const chapterReviewsQuery = chapterToQuery
      ? query(collection(db, "reviews"), where("chapter", "==", chapterToQuery))
      : query(collection(db, "reviews")); // SuperAdmin gets all
    const chapterReviewsSnap = await getDocs(chapterReviewsQuery);
    const commentsMap = {};
    chapterReviewsSnap.forEach(docSnap => {
      const data = docSnap.data();
      if (data.comments && data.comments.trim() !== "" && data.pitchId) {
        if (!commentsMap[data.pitchId]) commentsMap[data.pitchId] = [];
        commentsMap[data.pitchId].push({
          comment: data.comments.trim(),
          reviewerName: data.reviewerName || 'LP',
          reviewerId: data.reviewerId
        });
      }
    });
    setPitchComments(commentsMap);
  } catch (commentsError) {
    console.warn("LPPortal: Could not load team notes:", commentsError.message);
    setPitchComments({});
  }
}, [user, isSuperAdmin, userChapter]);

const loadAdminData = useCallback(async () => {
  if (!user || !isAdmin) {
    return; 
  }

  const chapterForAdminQuery = isSuperAdmin ? null : userChapter;
  // Admin users MUST have a chapter unless they are SuperAdmin
  if (!chapterForAdminQuery && !isSuperAdmin) {
    console.warn(`LPPortal: Skipping Admin data load: No chapter assigned for Admin user ${user.email}`);
    setAdminPitches([]); setUsers([]); setAllReviewsData([]); setAdminNotes([]);
    return;
  }

  let success = true;

  // Load Admin Pitches (Filtered by chapter for Admin, all for SuperAdmin)
  try {
    let adminPitchQuery = chapterForAdminQuery
      ? query(collection(db, "pitches"), where("chapter", "==", chapterForAdminQuery), orderBy("createdAt", "desc"))
      : query(collection(db, "pitches"), orderBy("createdAt", "desc")); // SuperAdmin gets all
    const adminPitchSnap = await getDocs(adminPitchQuery);
    const adminPitchDocs = adminPitchSnap.docs.map((d) => {
      const data = d.data();
      const createdDateValue = data.createdAt;
      const quarter = getQuarterFromDate(createdDateValue);

      return {
        id: d.id,
        ...data,
        createdAt: createdDateValue,
        createdDate: createdDateValue,
        quarter: quarter
      };
    });
    setAdminPitches(adminPitchDocs);
  } catch (error) {
    console.error(`LPPortal: Error loading ADMIN pitches for ${user.email} / chapter ${chapterForAdminQuery}:`, error.code, error.message);
    setAdminPitches([]); success = false;
    showAppAlert(`Error loading admin pitches: ${error.message}. Check Firestore Rules/Indexes.`);
  }

  // Load Users (Fetch ALL users regardless of admin chapter - needed for review lookups etc.)
  try {
    let usersQuery = query(collection(db, "users"), orderBy("name", "asc"));
    const usersSnap = await getDocs(usersQuery);
    const usersList = usersSnap.docs
      .map(d => ({ id: d.id, ...d.data(), uid: d.id }));
    setUsers(usersList);
    
    // Filter chapter members for the Chapter Members tab
    if (user?.chapter) {
      const chapterMembersList = usersList.filter(u =>
        u.chapter === user.chapter &&
        ['lp', 'chapter_director', 'superAdmin'].includes(u.role)
      );
      setChapterMembers(chapterMembersList);
    }
  } catch (error) {
    console.error(`LPPortal: Error loading ALL users for admin panel:`, error.code, error.message);
    setUsers([]); success = false;
    showAppAlert(`Error loading users list: ${error.message}. Check Firestore Rules.`);
  }

  // Load Reviews for admin summary (chapter-scoped for Admins, all for SuperAdmin)
  try {
    const allReviewsQuery = chapterForAdminQuery
      ? query(collection(db, "reviews"), where("chapter", "==", chapterForAdminQuery), orderBy("submittedAt", "desc"))
      : query(collection(db, "reviews"), orderBy("submittedAt", "desc"));
    const allReviewsSnap = await getDocs(allReviewsQuery);
    const allReviewsList = allReviewsSnap.docs.map(d => ({ reviewId: d.id, ...d.data(), submittedAt: d.data().submittedAt /* Store raw */ }));
    setAllReviewsData(allReviewsList);
  } catch (error) {
    console.error(`LPPortal: Error loading ALL reviews summary:`, error.code, error.message);
    setAllReviewsData([]); success = false;
    showAppAlert(`Error loading reviews summary: ${error.message}. Check Firestore Rules.`);
  }

  // Load admin discussion notes (chapter-scoped for chapter directors, all for SuperAdmin).
  try {
    const adminNotesQuery = chapterForAdminQuery
      ? query(collection(db, "adminNotes"), where("chapter", "==", chapterForAdminQuery), orderBy("createdAt", "desc"))
      : query(collection(db, "adminNotes"), orderBy("createdAt", "desc"));
    const adminNotesSnap = await getDocs(adminNotesQuery);
    const adminNotesList = adminNotesSnap.docs.map(d => ({ id: d.id, ...d.data() }));
    setAdminNotes(adminNotesList);
  } catch (error) {
    console.error(`LPPortal: Error loading admin notes:`, error.code, error.message);
    setAdminNotes([]);
    // Non-fatal — admin panel should still function if notes fail to load.
  }

  if (success) {
  } else {
    console.warn(`LPPortal: Admin Data loading attempt finished with errors.`);
  }

}, [user, isAdmin, isSuperAdmin, userChapter]);

const loadChapterMembers = useCallback(async () => {
  if (!user || !user.chapter) {
    return;
  }

  try {
    
    // Query users from the same chapter - simplified to avoid index requirements
    const usersQuery = query(
      collection(db, "users"),
      where("chapter", "==", user.chapter)
    );
    
    const usersSnap = await getDocs(usersQuery);
    
    // Filter for active roles after fetching
    const chapterMembersList = usersSnap.docs
      .map(d => ({ 
        id: d.id, 
        ...d.data(), 
        uid: d.id 
      }))
      .filter(u => ["lp", "chapter_director", "superAdmin"].includes(u.role))
      .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    
    setChapterMembers(chapterMembersList);
  } catch (error) {
    console.error(`LPPortal: Error loading chapter members:`, error);
    console.error(`LPPortal: Error details - Code: ${error.code}, Message: ${error.message}`);
    setChapterMembers([]);
    
    // Provide specific error guidance
    if (error.code === 'permission-denied') {
      console.error("LPPortal: Permission denied - LP users may not have read access to users collection");
      console.error("LPPortal: IMPORTANT - Update Firestore rules to allow LPs to read users in their chapter");
      
      // Temporary fallback: Show at least the current user
      if (user) {
        setChapterMembers([{
          id: user.uid,
          uid: user.uid,
          name: user.name || user.email,
          email: user.email,
          role: user.role,
          chapter: user.chapter,
          badges: user.badges || [],
          anniversary: user.anniversary
        }]);
      }
    } else if (error.message?.includes('index')) {
    }
  }
}, [user]);

// Loads the /chapters collection. Safe for any authenticated user (read rule is
// public). Sorted by `order`, then `name`. Populates dropdowns across the portal.
const loadChapters = useCallback(async () => {
  try {
    const chaptersSnap = await getDocs(collection(db, "chapters"));
    const chaptersList = chaptersSnap.docs
      .map(d => ({ id: d.id, ...d.data() }))
      .sort((a, b) => {
        const ao = typeof a.order === 'number' ? a.order : 999;
        const bo = typeof b.order === 'number' ? b.order : 999;
        if (ao !== bo) return ao - bo;
        return (a.name || '').localeCompare(b.name || '');
      });
    setChapters(chaptersList);
    setChaptersLoaded(true);
  } catch (error) {
    console.error('LPPortal: Error loading chapters collection:', error);
    setChapters([]);
    setChaptersLoaded(true); // fall back to hardcoded CHAPTERS constant in UI
  }
}, []);

const loadBulletinMessages = useCallback(async () => {
  if (!user || !user.chapter) {
    return;
  }

  try {
    
    // Query bulletin messages for the user's chapter
    const messagesQuery = query(
      collection(db, "bulletinMessages"),
      where("authorChapter", "==", user.chapter),
      orderBy("timestamp", "desc")
    );
    
    const messagesSnap = await getDocs(messagesQuery);
    
    const messagesList = messagesSnap.docs.map(d => ({
      id: d.id,
      ...d.data(),
      timestamp: d.data().timestamp?.toDate ? d.data().timestamp.toDate() : new Date(d.data().timestamp)
    }));
    
    setBulletinMessages(messagesList);
  } catch (error) {
    console.error(`LPPortal: Error loading bulletin messages:`, error);
    setBulletinMessages([]);
    
    // Handle index error
    if (error.message?.includes('index')) {
      showAppAlert("Please create a Firestore index for bulletinMessages with authorChapter and timestamp fields.");
    }
  }
}, [user]);

useEffect(() => {
  // Only load data if auth check is done AND we have a valid user object
  if (!isLoadingAuth && user && user.uid && user.role) {
    loadLPData(); // Load data relevant to LPs (pitches for their chapter, their reviews)
    loadChapterMembers(); // Load chapter members for all authenticated users
    loadBulletinMessages(); // Load bulletin messages for all authenticated users
    loadChapters(); // Load chapters collection for dropdowns + management UI
    if (isAdmin) {
      loadAdminData();
    }
  } else if (!isLoadingAuth) {
    // If auth check is done but we DON'T have a user, ensure data is cleared
    setLpPitches([]);
    setLpPitchesLoaded(false);
    setAdminPitches([]);
    setReviews({});
    setSelectedPitch(null);
    setReviewFormData({});
    setUsers([]);
    setAllReviewsData([]);
    setAdminNotes([]);
    setChapterMembers([]);
    setBulletinMessages([]);
  }

  // This effect runs when user or isLoadingAuth changes.
}, [user, isLoadingAuth, isAdmin, loadLPData, loadAdminData, loadChapterMembers, loadBulletinMessages, loadChapters]);

// Update last viewed timestamp when viewing Message Board
useEffect(() => {
  if (user && activeTab === 'bulletinBoard') {
    const now = new Date().toISOString();
    setLastViewedTimestamp(now);
    localStorage.setItem(`messageBoard_lastViewed_${user.uid}`, now);
  }
}, [user, activeTab]);

// Load resources when admin panel is active. Chapter directors manage
// their own chapter's resources from the same tab, so the load needs to
// fire for them too — the UI scopes the table client-side and firestore
// rules enforce write boundaries by chapter.
useEffect(() => {
  if (user && activeTab === 'adminPanel' && activeAdminTab === 'resourcesManagement' && (isSuperAdmin || isChapterDirector)) {
    loadManagedResources();
  }
}, [user, activeTab, activeAdminTab, isSuperAdmin, isChapterDirector]);

useEffect(() => {
  if (selectedPitch && selectedPitch.id) {
      // Check if reviews state has keys before trying to access
      if (Object.keys(reviews).length > 0) {
          const userReview = reviews[selectedPitch.id];
          if (userReview) {
              // Populate form with existing review data
              setReviewFormData({
                  pitchVideoRating: userReview.pitchVideoRating || "",
                  businessModelRating: userReview.businessModelRating || "",
                  productMarketFitRating: userReview.productMarketFitRating || "",
                  overallLpRating: userReview.overallLpRating || "",
                  comments: userReview.comments || ""
              });
          } else {
              // Clear form (no review found for this pitch)
              setReviewFormData({ pitchVideoRating: "", businessModelRating: "", productMarketFitRating: "", overallLpRating: "", comments: "" });
          }
      } else {
          // reviews object is empty, wait for it to load
          // Clear form while waiting
          setReviewFormData({ pitchVideoRating: "", businessModelRating: "", productMarketFitRating: "", overallLpRating: "", comments: "" });
      }
  } else {
      // No pitch selected, clear the form
      setReviewFormData({ pitchVideoRating: "", businessModelRating: "", productMarketFitRating: "", overallLpRating: "", comments: "" });
  }
}, [selectedPitch, reviews]);

// Switching pitches (or closing the panel) invalidates any in-flight submit
// feedback — reset the button and drop a pending success-hold timer so it
// can't close a pitch the user just navigated to.
useEffect(() => {
  setReviewSubmitPhase('idle');
  return () => {
    if (reviewSuccessTimerRef.current) {
      clearTimeout(reviewSuccessTimerRef.current);
      reviewSuccessTimerRef.current = null;
    }
  };
}, [selectedPitch?.id]);


// Effect 4: Restore scroll position when returning to the list
useEffect(() => {
    if (!selectedPitch && listScrollRef.current && listScrollPosition > 0) {
      
      const timer = setTimeout(() => {
        if (listScrollRef.current) {
          listScrollRef.current.scrollTop = listScrollPosition;
        }
        setListScrollPosition(0); // Reset after restoring
      }, 50); 
      return () => clearTimeout(timer);
    }
}, [selectedPitch, listScrollPosition]); 

// --- Multi-Select Dropdown Component ---
const MultiSelectDropdown = ({ options, selected, onChange, placeholder = "Select options" }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleOption = (option) => {
    if (selected.includes(option)) {
      onChange(selected.filter(item => item !== option));
    } else {
      onChange([...selected, option]);
    }
  };

  const selectAll = () => {
    onChange(options);
  };

  const deselectAll = () => {
    onChange([]);
  };

  const getDisplayText = () => {
    if (selected.length === 0 || selected.length === options.length) {
      return placeholder;
    } else if (selected.length === 1) {
      return selected[0];
    } else {
      return `${selected.length} selected`;
    }
  };

  return (
    <div ref={dropdownRef} className="multi-select-dropdown" style={{ position: 'relative', display: 'inline-block' }}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="win95-select"
        style={{
          minWidth: '130px',
          width: '100%',
          cursor: 'pointer',
          padding: 0,
          color: 'var(--chrome-outline)',
          textAlign: 'left'
        }}
      >
        <span style={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          alignItems: 'center',
          padding: '0 26px 0 8px',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap'
        }}>{getDisplayText()}</span>
        <span className="win95-select__arrow" aria-hidden="true" />
      </button>

      {isOpen && (
        <div className="multi-select-panel" style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          marginTop: '2px',
          minWidth: '200px',
          maxHeight: '300px',
          overflowY: 'auto',
          zIndex: 1000,
          background: 'var(--gnf-bg)',
          border: '2px solid',
          borderColor: 'var(--bevel-raised)',
          boxShadow: 'var(--shadow-raised)',
          fontFamily: 'var(--font-chrome)',
          fontSize: '12px'
        }}>
          <div style={{
            padding: '6px 8px',
            borderBottom: '1px solid var(--mb-ink-15)',
            display: 'flex',
            justifyContent: 'space-between',
            background: 'var(--mb-paper)'
          }}>
            <button
              onClick={selectAll}
              style={{
                background: 'none',
                border: 'none',
                color: 'var(--mb-magenta)',
                cursor: 'pointer',
                fontSize: '11px',
                fontFamily: 'var(--font-content)',
                fontWeight: 'bold',
                padding: '2px 6px'
              }}
            >
              Select All
            </button>
            <button
              onClick={deselectAll}
              style={{
                background: 'none',
                border: 'none',
                color: 'var(--mb-ink-60)',
                cursor: 'pointer',
                fontSize: '11px',
                fontFamily: 'var(--font-content)',
                fontWeight: 'bold',
                padding: '2px 6px'
              }}
            >
              Clear All
            </button>
          </div>
          {options.map((option) => (
            <label
              key={option}
              style={{
                display: 'block',
                padding: '8px 12px',
                cursor: 'pointer',
                transition: 'none',
                fontSize: '14px',
                color: 'var(--mb-ink)'
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = 'var(--mb-butter)'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
            >
              <input
                type="checkbox"
                checked={selected.includes(option)}
                onChange={() => toggleOption(option)}
                style={{ width: '14px', height: '14px', flexShrink: 0, margin: '0 8px 0 0', verticalAlign: 'middle' }}
              />
              {option}
            </label>
          ))}
        </div>
      )}
    </div>
  );
};


// Social Cards drawing + download logic lives in ./tabs/SocialCardsTab.jsx —
// the component mounts only while the tab is active, which replaces the old
// activeTab === 'socialCards' effect gate.

// --- Helper Functions ---

const getNextUnreviewedPitch = () => {
  if (!lpPitches || lpPitches.length === 0) return null;
  
  // Get currently filtered pitches (same logic as the main filter)
  const filteredPitches = lpPitches.filter((p) => {
    // Manually logged grant awards are not applications — never reviewable.
    if (isManualAward(p)) return false;

    const review = reviews[p.id];

    // Filter by Chapter
    const matchesChapter = !reviewChapterFilter || p.chapter === reviewChapterFilter;
    if (!matchesChapter) return false;

    // Filter by Quarter (multi-select)
    const matchesQuarter = reviewQuarterFilter.length === 0 || reviewQuarterFilter.includes(p.quarter);
    if (!matchesQuarter) return false;

    // Filter by Category
    if (reviewCategoryFilter && p.category !== reviewCategoryFilter) return false;

    // Filter by Review Status Dropdown
    if (reviewFilter === "reviewed" && !review) return false;
    if (reviewFilter === "notReviewed" && review) return false;

    // Hide Passed Reviews Filter
    if (hidePassedReviews && review && (review.overallLpRating === "Pass" || review.overallLpRating === "Ineligible")) {
      return false;
    }

    // Search Term Filter
    const searchLower = reviewSearchTerm.toLowerCase();
    if (searchLower && !(
      (p.businessName && p.businessName.toLowerCase().includes(searchLower)) ||
      (p.founderName && p.founderName.toLowerCase().includes(searchLower)) ||
      (p.email && p.email.toLowerCase().includes(searchLower))
    )) {
      return false;
    }

    return true;
  });

  // Find the current pitch index in filtered results
  const currentIndex = filteredPitches.findIndex(p => p.id === selectedPitch?.id);
  
  // Look for next unreviewed pitch starting from current position
  for (let i = currentIndex + 1; i < filteredPitches.length; i++) {
    const pitch = filteredPitches[i];
    if (!reviews[pitch.id]) {
      return pitch;
    }
  }
  
  // If no unreviewed pitch found after current, wrap around to beginning
  for (let i = 0; i < currentIndex; i++) {
    const pitch = filteredPitches[i];
    if (!reviews[pitch.id]) {
      return pitch;
    }
  }
  
  return null; // No unreviewed pitches found
};

// Helper function to handle tab changes (closes mobile menu)
const handleTabChange = (tab) => {
  setActiveTab(tab);
  if (isMobile()) {
    setIsMobileMenuOpen(false);
  }
};

// Bulletin board post / delete / pin / react handlers live in
// ./tabs/BulletinBoardTab.jsx — only that tab uses them. Message state stays
// here (loaded by loadBulletinMessages, read by the newMessageCount memo).

// Resources Management Functions
const loadManagedResources = async () => {
  try {
    const resourcesRef = collection(db, 'resources');
    const q = query(resourcesRef, orderBy('Resource'));
    const querySnapshot = await getDocs(q);
    
    const resources = [];
    querySnapshot.forEach((doc) => {
      resources.push({
        id: doc.id,
        ...doc.data()
      });
    });
    
    setManagedResources(resources);
  } catch (error) {
    console.error("LPPortal: Error loading resources:", error);
    showAppAlert("Failed to load resources.");
  }
};


// Navigation items component (shared between desktop and mobile).
// Chrome sidebar: user header, icon nav rows, then the pinned footer.
const PORTAL_NAV_ITEMS = [
  { key: 'reviewPitches', label: 'Review pitches', icon: 'pitch' },
  { key: 'pitchMap', label: 'Pitch map', icon: 'map' },
  { key: 'badges', label: 'Trophy case', icon: 'trophy' },
  { key: 'statistics', label: 'Statistics', icon: 'sheet' },
  { key: 'chapterMembers', label: 'Chapter members', icon: 'chapters' },
  { key: 'socialCards', label: 'Social cards', icon: 'note' },
  { key: 'resources', label: 'Resources', icon: 'resources' },
];

const NavigationItems = () => (
  <>
    <div className="win95-portal-user">
      <OSIcon32 kind="computer" size={22} />
      <div style={{ minWidth: 0, flex: 1 }}>
        <div className="win95-portal-user__name">{user?.name || user?.email}</div>
        <div className="win95-portal-user__role">
          {isSuperAdmin ? 'Super admin' : isChapterDirector ? 'Chapter director' : 'Limited partner'}
          {user?.chapter ? ` · ${user.chapter}` : ''}
        </div>
        {/* Logout lives here, under the identity it signs out — the title
            bar holds only window controls, like every other window. */}
        <button
          type="button"
          className="win95-btn win95-btn--compact"
          onClick={handleSignOut}
          style={{ marginTop: 6, textTransform: 'none' }}
        >
          Logout
        </button>
      </div>
      {/* Mobile close button */}
      <button
        onClick={() => setIsMobileMenuOpen(false)}
        className="mobile-close-btn win95-btn-sys"
        style={{ display: 'none' }}
        aria-label="Close menu"
      >
        ✕
      </button>
    </div>

    <div className="win95-portal-nav">
      {PORTAL_NAV_ITEMS.map((item) => (
        <button
          key={item.key}
          onClick={() => handleTabChange(item.key)}
          className={`win95-portal-nav__item ${activeTab === item.key ? 'is-active' : ''}`}
          aria-current={activeTab === item.key ? 'page' : undefined}
        >
          <OSIcon32 kind={item.icon} size={17} />
          {item.label}
        </button>
      ))}
      {/* Admin panel - visible to chapter directors and super admins */}
      {isAdmin && (
        <button
          onClick={() => handleTabChange('adminPanel')}
          className={`win95-portal-nav__item ${activeTab === 'adminPanel' ? 'is-active' : ''}`}
          aria-current={activeTab === 'adminPanel' ? 'page' : undefined}
        >
          <OSIcon32 kind="portal" size={17} />
          Admin panel
        </button>
      )}
    </div>

    {/* Pinned footer: quarter progress, membership links, community, chapter info */}
    <div className="win95-portal-foot">
      {(() => {
        const currentQuarter = getCurrentQuarter();
        const quarterReviews = userStats?.quarterlyReviews?.[currentQuarter] || 0;
        const totalReviews = userStats?.totalReviews || 0;
        // Milestone ladder mirrors the review-count badges in badgeDefinitions.
        const milestones = [
          { count: 1, name: 'First Review' },
          { count: 10, name: 'New Kid on the Block' },
          { count: 25, name: 'Neighborhood Watch' },
          { count: 50, name: 'Review Pro' },
          { count: 75, name: 'Review Champion' },
          { count: 100, name: 'Platinum Status' },
          { count: 200, name: 'Review Legend' },
          { count: 500, name: 'Grand Master' },
        ];
        const nextMilestone = milestones.find(m => m.count > totalReviews);
        const PROGRESS_BLOCKS = 14;
        const filledBlocks = nextMilestone
          ? Math.min(PROGRESS_BLOCKS, Math.floor((totalReviews / nextMilestone.count) * PROGRESS_BLOCKS))
          : PROGRESS_BLOCKS;
        return (
          <fieldset className="win95-groupbox">
            <legend>My quarter</legend>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4 }}>
              <div className="win95-lcd">
                <div className="win95-lcd__label">Reviewed</div>
                {quarterReviews}
              </div>
              <div className="win95-lcd">
                <div className="win95-lcd__label">All time</div>
                {totalReviews}
              </div>
            </div>
            {nextMilestone && (
              <>
                <div style={{
                  fontFamily: 'var(--font-chrome)',
                  fontSize: '11px',
                  lineHeight: 1.35,
                  margin: '8px 0 5px'
                }}>
                  {nextMilestone.count - totalReviews} more {nextMilestone.count - totalReviews === 1 ? 'review unlocks' : 'reviews unlock'}{' '}
                  <strong>{nextMilestone.name}</strong>
                </div>
                <div
                  className="win95-progress"
                  role="progressbar"
                  aria-valuemin={0}
                  aria-valuemax={nextMilestone.count}
                  aria-valuenow={totalReviews}
                  aria-label={`Progress toward ${nextMilestone.name}`}
                >
                  {Array.from({ length: PROGRESS_BLOCKS }, (_, i) => (
                    <span key={i} className={`win95-progress__block ${i < filledBlocks ? 'is-filled' : ''}`} />
                  ))}
                </div>
              </>
            )}
          </fieldset>
        );
      })()}

      {(() => {
        // Chapter-doc stripeLinks (written by the New Chapter wizard /
        // provisionChapterStripe callable) win over the static chapterConfig
        // links, so wizard-created chapters get renewal buttons with no deploy.
        const chapterDoc = chapters.find(c => c.name === user?.chapter);
        const docLinks = chapterDoc?.stripeLinks || {};
        const configLinks = getChapterMembershipLinks(user?.chapter) || {};
        const links = {
          annualUrl: docLinks.lpAnnualUrl || configLinks.annualUrl,
          semiAnnualUrl: docLinks.lpSemiAnnualUrl || configLinks.semiAnnualUrl,
        };
        if (!links.annualUrl && !links.semiAnnualUrl) return null;
        const linkBtnStyle = { display: 'block', textAlign: 'center', textDecoration: 'none', marginTop: 6, boxSizing: 'border-box' };
        return (
          <fieldset className="win95-groupbox">
            <legend>LP membership</legend>
            {links.annualUrl && (
              <a href={links.annualUrl} target="_blank" rel="noopener noreferrer" className="win95-btn" style={linkBtnStyle}>
                Renew annual
              </a>
            )}
            {links.semiAnnualUrl && (
              <a href={links.semiAnnualUrl} target="_blank" rel="noopener noreferrer" className="win95-btn" style={linkBtnStyle}>
                Renew semi-annual
              </a>
            )}
          </fieldset>
        );
      })()}

      {/* Slack community invite */}
      <fieldset className="win95-groupbox">
        <legend>Community</legend>
        <a
          href="https://join.slack.com/t/goodneighborfund/shared_invite/zt-3w6mgybja-8ASKwhvUyXsrFRlJ4uxdLQ"
          target="_blank"
          rel="noopener noreferrer"
          className="win95-btn"
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7, textDecoration: 'none', marginTop: 2, boxSizing: 'border-box' }}
        >
          {/* Monochrome Slack mark — single currentColor path, per the
              one-ink icon treatment chrome buttons use */}
          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" style={{ flex: 'none' }}>
            <path d="M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52zm1.271 0a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.834 24a2.528 2.528 0 0 1-2.521-2.522v-6.313zM8.834 5.042a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.834 0a2.528 2.528 0 0 1 2.521 2.522v2.52H8.834zm0 1.271a2.528 2.528 0 0 1 2.521 2.521 2.528 2.528 0 0 1-2.521 2.521H2.522A2.528 2.528 0 0 1 0 8.834a2.528 2.528 0 0 1 2.522-2.521h6.312zm10.122 2.521a2.528 2.528 0 0 1 2.522-2.521A2.528 2.528 0 0 1 24 8.834a2.528 2.528 0 0 1-2.522 2.521h-2.522V8.834zm-1.268 0a2.528 2.528 0 0 1-2.523 2.521 2.527 2.527 0 0 1-2.52-2.521V2.522A2.527 2.527 0 0 1 15.165 0a2.528 2.528 0 0 1 2.523 2.522v6.312zm-2.523 10.122a2.528 2.528 0 0 1 2.523 2.522A2.528 2.528 0 0 1 15.165 24a2.527 2.527 0 0 1-2.52-2.522v-2.522h2.52zm0-1.268a2.527 2.527 0 0 1-2.52-2.523 2.526 2.526 0 0 1 2.52-2.52h6.313A2.527 2.527 0 0 1 24 15.165a2.528 2.528 0 0 1-2.522 2.523h-6.313z" />
          </svg>
          Join Slack
        </a>
      </fieldset>

      {/* Chapter info */}
      <fieldset className="win95-groupbox">
        <legend>Chapter</legend>
        <div style={{ fontFamily: 'var(--font-chrome)', fontSize: '12px', fontWeight: 700 }}>
          {user?.chapter || 'Not assigned'}
        </div>
        <div style={{
          fontFamily: 'var(--font-chrome)',
          fontSize: '10px',
          fontWeight: 700,
          color: 'var(--chrome-text-soft)',
          margin: '8px 0 2px'
        }}>Member since</div>
        <div style={{ fontFamily: 'var(--font-numeral)', fontSize: '11px' }}>
          {user?.anniversary ? new Date(user.anniversary.seconds * 1000).toLocaleDateString('en-US', { year: 'numeric', month: 'long' }) : 'Unknown'}
        </div>
      </fieldset>
    </div>
  </>
);

// --- Helper Functions ---

const formatDate = (ts) => {
  if (!ts) return "No date";
  try {
    let date;
    
    if (typeof ts.toDate === 'function') {
      date = ts.toDate();
    
    } else if (ts instanceof Date) {
      date = ts;
    
    } else {
      console.warn("formatDate: Input was not a Firestore Timestamp or JS Date, attempting conversion:", ts);
      date = new Date(ts);
    }

    
    if (isNaN(date.getTime())) {
      console.error("formatDate resulted in an invalid date for input:", ts);
      return "Invalid Date";
    }

    
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  } catch (error) {
    
    console.error("Error formatting date:", error, "Input:", ts);
    return "Invalid Date";
  }
};

// --- Handlers ---

// Auth Handlers
// Magic-link sign-in: user enters their email and we ask the cloud function
// to mint + email a Firebase email-link. The link routes back to /portal,
// where the useEffect below picks it up and calls signInWithEmailLink.
const handleSendSignInLink = async () => {
  setAuthError("");
  const requestEmail = email.trim().toLowerCase();
  if (!requestEmail || !requestEmail.includes("@")) {
    setAuthError("Enter a valid email address.");
    return;
  }
  setSignInLinkSending(true);
  try {
    await sendSignInLinkCallable({ email: requestEmail });
    // Stash the email so the return-trip handler can complete sign-in even if
    // the user opens the link on a device that doesn't have it cached yet.
    window.localStorage.setItem(EMAIL_FOR_SIGN_IN_KEY, requestEmail);
    setSignInLinkSentTo(requestEmail);
  } catch (err) {
    console.error("LPPortal: sendSignInLink error:", err);
    setAuthError(err?.message || "Couldn't send sign-in link. Try again in a moment.");
  } finally {
    setSignInLinkSending(false);
  }
};

const handleGoogleLogin = async () => {
  try {
    setAuthError("");
    await signInWithPopup(auth, provider);
    
  } catch (err) {
    console.error("LPPortal: Google Login Error:", err);
    if (err.code === 'auth/popup-closed-by-user') {
      
    } else if (err.code === 'auth/account-exists-with-different-credential') {
      setAuthError("This email is already linked to a different sign-in method. Try the magic-link option instead.");
    } else {
      setAuthError(`Google login failed: ${err.message}`);
    }
  }
};

const handleSignOut = async () => {
  try {
    await signOut(auth);
    // Clear session storage flags
    sessionStorage.removeItem('lpPortalRedirected');
    sessionStorage.removeItem('lpPortalAuthenticated');
    // If in standalone mode, redirect to home
    if (isStandalone) {
      window.location.href = '/';
    }
    // State clearing handled by onAuthStateChanged
  } catch (error) {
    console.error("LPPortal: Sign out Error:", error);
    showAppAlert(`Sign out failed: ${error.message}`);
  }
};

// Title-bar Close: leave the portal (still signed in) and land on a clean
// desktop with no windows open. The launch key's "none" tells
// DesktopEnvironment to skip its default Navigator window; marking the
// session booted skips the boot screen.
const handleClosePortal = () => {
  try {
    sessionStorage.setItem(DESKTOP_LAUNCH_KEY, 'none');
    sessionStorage.setItem('gnf-has-booted', '1');
  } catch {}
  window.location.href = '/';
};

// Removed handleInviteCodeSubmit - no longer using invite codes


// LP Review Handlers
const handleReviewSubmit = async (e) => {
  e.preventDefault();
  if (reviewSubmitPhase !== 'idle') return; // Write already in flight (or success hold) — ignore repeat clicks
  if (!user || !selectedPitch) {
    console.error("Review submit cancelled: Missing user or selected pitch.");
    showAppAlert("Cannot submit review: User or Pitch data missing. Please select a pitch first.", { variant: "error" });
    return;
  }


  const reviewId = `${user.uid}_${selectedPitch.id}`;


  if (!reviewId || reviewId.includes('<span') || !selectedPitch.id || !user.uid) { 
    console.error("LPPortal: Invalid reviewId generated!", reviewId);
    showAppAlert("Failed submit. Critical error generating review ID.", { variant: "error" });
    return; 
  }


  const previousReview = reviews[selectedPitch.id] || null;
  const isEdit = !!previousReview;
  const nextEditCount = (previousReview?.editCount || 0) + (isEdit ? 1 : 0);

  setReviewSubmitWasEdit(isEdit);
  setReviewSubmitPhase('saving');

  const newReview = {
    reviewerId: user.uid,
    reviewerName: user.name || user.email, // Store reviewer name/email for easier display later if needed
    pitchId: selectedPitch.id,
    pitchBusinessName: selectedPitch.businessName || "Unknown Business",
    chapter: selectedPitch.chapter || user.chapter || "Unknown",
    quarter: selectedPitch.quarter || null, // Needed for quarter-scoped badge stats (Midas Touch, Completionist)
    editCount: nextEditCount, // Persisted on review doc; used by trackReviewSubmission for maxReviewEdits
    ...reviewFormData,
    submittedAt: Timestamp.fromDate(new Date()),
    lastUpdatedAt: Timestamp.fromDate(new Date())
  };

  try {
    const reviewDocRef = doc(db, "reviews", reviewId);
    // Use setDoc with merge:true to either create or overwrite the review
    await setDoc(reviewDocRef, newReview, { merge: true });

    // --- Success ---

    // The review is saved — confirm to the user now. Stats + badge tracking
    // runs several extra Firestore round trips; it must not delay (or, on
    // failure, misreport) the confirmation, so it runs in the background and
    // the badge modal simply pops when it's ready.
    setReviewSubmitPhase('saved');
    showAppAlert(isEdit ? "Review updated." : "Review submitted!", { variant: "success" });

    trackReviewSubmission(user.uid, newReview, selectedPitch, isEdit, previousReview)
      .then((trackingResult) => {
        if (trackingResult.newBadges && trackingResult.newBadges.length > 0) {
          // Show badge notification
          setShowBadgeNotification(trackingResult.newBadges[0]);
          setUserBadges(prev => [...prev, ...trackingResult.newBadges]);
        }
        setUserStats(trackingResult.stats);
      })
      .catch((trackErr) => {
        console.error("LPPortal: stats/badge tracking failed (review itself saved fine):", trackErr);
      });


    setReviews(currentReviews => {
        const updatedReviews = {
            ...currentReviews,
            [selectedPitch.id]: { reviewId: reviewId, ...newReview }
        };
        return updatedReviews;
    });

    // Update pitchComments so team notes reflect immediately
    if (newReview.comments && newReview.comments.trim() !== "") {
      setPitchComments(prev => {
        const existing = (prev[selectedPitch.id] || []).filter(c => c.reviewerId !== user.uid);
        return {
          ...prev,
          [selectedPitch.id]: [...existing, {
            comment: newReview.comments.trim(),
            reviewerName: user.name || user.email,
            reviewerId: user.uid
          }]
        };
      });
    } else {
      // Remove user's comment if they cleared it
      setPitchComments(prev => ({
        ...prev,
        [selectedPitch.id]: (prev[selectedPitch.id] || []).filter(c => c.reviewerId !== user.uid)
      }));
    }


    // Hold the "✓" success state on the button long enough to register, then
    // close the detail view. Guarded by pitch id: if the user has already
    // moved on (e.g. via Next Unreviewed), leave their new selection alone.
    const submittedPitchId = selectedPitch.id;
    reviewSuccessTimerRef.current = setTimeout(() => {
      reviewSuccessTimerRef.current = null;
      setSelectedPitch(prev => (prev && prev.id === submittedPitchId ? null : prev)); // Scroll position restoration handled by useEffect[selectedPitch]
      setReviewSubmitPhase('idle');
      // Form reset is handled by the useEffect hook watching selectedPitch
    }, 1100);

  } catch (error) {
    console.error(`LPPortal: Error submitting review ${reviewId}:`, error.code, error.message);
    setReviewSubmitPhase('idle'); // Re-enable the button so they can retry
    showAppAlert(`Failed submit. ${error.message}. Please try again.`, { variant: "error" });
  }
};


const handleReviewFormChange = (e) => {
  const { name, value } = e.target;
  setReviewFormData((prev) => ({ ...prev, [name]: value }));
};

// One-click overall rating from the list's action bar — same review doc and
// tracking pipeline as the full form, without opening the detail view.
// Existing optional ratings/comments on the review are preserved via merge.
const submitQuickRating = async (pitch, rating) => {
  if (!user || !pitch?.id) return;
  const reviewId = `${user.uid}_${pitch.id}`;
  const previousReview = reviews[pitch.id] || null;
  const isEdit = !!previousReview;

  const newReview = {
    reviewerId: user.uid,
    reviewerName: user.name || user.email,
    pitchId: pitch.id,
    pitchBusinessName: pitch.businessName || "Unknown Business",
    chapter: pitch.chapter || user.chapter || "Unknown",
    quarter: pitch.quarter || null,
    editCount: (previousReview?.editCount || 0) + (isEdit ? 1 : 0),
    overallLpRating: rating,
    submittedAt: Timestamp.fromDate(new Date()),
    lastUpdatedAt: Timestamp.fromDate(new Date())
  };

  try {
    await setDoc(doc(db, "reviews", reviewId), newReview, { merge: true });
    showAppAlert(
      isEdit
        ? `Rating updated: ${pitch.businessName || "pitch"} → ${rating}.`
        : `Rated ${pitch.businessName || "pitch"}: ${rating}.`,
      { variant: "success" }
    );

    trackReviewSubmission(user.uid, newReview, pitch, isEdit, previousReview)
      .then((trackingResult) => {
        if (trackingResult.newBadges && trackingResult.newBadges.length > 0) {
          setShowBadgeNotification(trackingResult.newBadges[0]);
          setUserBadges(prev => [...prev, ...trackingResult.newBadges]);
        }
        setUserStats(trackingResult.stats);
      })
      .catch((trackErr) => {
        console.error("LPPortal: stats/badge tracking failed (quick rating itself saved fine):", trackErr);
      });

    setReviews(currentReviews => ({
      ...currentReviews,
      [pitch.id]: { reviewId, ...(previousReview || {}), ...newReview }
    }));
  } catch (error) {
    console.error(`LPPortal: Error quick-rating ${reviewId}:`, error.code, error.message);
    showAppAlert(`Failed to save rating. ${error.message}. Please try again.`, { variant: "error" });
  }
};

const selectPitchForReview = (pitch) => {
  // Capture scroll position before changing state
  if (listScrollRef.current) {
      setListScrollPosition(listScrollRef.current.scrollTop);
  }
  setSelectedPitch(pitch);
  // Form data population is handled by useEffect watching selectedPitch and reviews
};

// Back to list handler - ensures scroll position logic runs
const handleBackToList = () => {
    setSelectedPitch(null);
    // Scroll restoration is handled by useEffect watching selectedPitch
};


// Admin Panel Handlers

// Generate a temporary password for new users
const generateTempPassword = () => {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789!@#$%^&*';
  let password = '';
  for (let i = 0; i < 12; i++) {
    password += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return password;
};








// Export the LP pitch list (all fields, based on current filters)
const handleLpPitchExport = () => {
  const pitchesToExport = lpFilteredPitches;
  if (!pitchesToExport || pitchesToExport.length === 0) {
    showAppAlert("No pitches match the current filters to export.");
    return;
  }

  const normalizeValue = (val) => {
    if (val === null || val === undefined) return "";
    if (typeof val === 'object') {
      if (typeof val.toDate === 'function') {
        try { return val.toDate().toISOString(); } catch { return ""; }
      }
      if (val instanceof Date) return val.toISOString();
      if (Array.isArray(val)) return val.join(", ");
      try { return JSON.stringify(val); } catch { return String(val); }
    }
    return val;
  };

  // Collect the union of all keys across pitches for "all fields"
  const allKeys = new Set();
  pitchesToExport.forEach((p) => Object.keys(p || {}).forEach((k) => allKeys.add(k)));
  const orderedKeys = Array.from(allKeys).sort();

  const rows = pitchesToExport.map((p) => {
    const row = {};
    orderedKeys.forEach((k) => { row[k] = normalizeValue(p[k]); });
    const myReview = reviews[p.id];
    row["myReviewRating"] = myReview?.overallLpRating || "";
    row["myReviewComments"] = myReview?.comments || "";
    row["mySubmittedAt"] = myReview?.submittedAt ? normalizeValue(myReview.submittedAt) : "";
    return row;
  });

  try {
    const csv = Papa.unparse(rows, { header: true });
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const timestamp = new Date().toISOString().slice(0, 10);
    const chapterPart = reviewChapterFilter || 'allChapters';
    const quarterPart = reviewQuarterFilter.length === 0 ? 'allQuarters' : reviewQuarterFilter.join('-');
    const filename = `neighborhoodOS_review_pitches_${chapterPart}_${quarterPart}_${timestamp}.csv`;
    saveAs(blob, filename);
  } catch (error) {
    console.error("LPPortal: LP CSV Export Error:", error);
    showAppAlert(`CSV export failed: ${error.message}`);
  }
};


// --- Memos for derived data ---

// Memoized list of LP pitches filtered by current UI settings
const lpFilteredPitches = useMemo(() => {
  if (!Array.isArray(lpPitches)) return [];

  return lpPitches.filter((p) => {
    // Ensure pitch has an ID
    if (!p.id) return false;

    // Manually logged grant awards are not applications — never reviewable.
    if (isManualAward(p)) return false;

    // Access review using pitch ID. Make sure reviews object is populated correctly.
    const review = reviews[p.id];

    // Filter by Search Term (Business Name or Founder Name)
    const searchTermLower = reviewSearchTerm.toLowerCase();
    const matchesSearch = !searchTermLower || p.businessName?.toLowerCase().includes(searchTermLower) || p.founderName?.toLowerCase().includes(searchTermLower);
    if (!matchesSearch) return false;

    // Filter by Chapter
    const matchesChapter = !reviewChapterFilter || p.chapter === reviewChapterFilter;
    if (!matchesChapter) return false;

    // Filter by Quarter (multi-select)
    const matchesQuarter = reviewQuarterFilter.length === 0 || reviewQuarterFilter.includes(p.quarter);
    if (!matchesQuarter) return false;

    // Filter by Category
    if (reviewCategoryFilter && p.category !== reviewCategoryFilter) return false;

    // Filter by Review Status Dropdown
    if (reviewFilter === "reviewed" && !review) return false;
    if (reviewFilter === "notReviewed" && review) return false;
    if (reviewFilter === "favorites" && (!review || review.overallLpRating !== "Favorite")) return false;

    // Filter by "Hide Passed/Ineligible" Toggle
    if (hidePassedReviews && review && ["Pass", "Ineligible"].includes(review.overallLpRating)) {
      return false;
    }

    // If it passed all filters, include it
    return true;
  });
}, [lpPitches, reviews, reviewSearchTerm, reviewFilter, reviewChapterFilter, reviewQuarterFilter, reviewCategoryFilter, hidePassedReviews]);




// Memoized sorted lists for display
const sortedUsers = useMemo(() => {
  if (!Array.isArray(users)) return [];
  // Sort primarily by name, fallback to email if name is missing
  return [...users].sort((a, b) => {
    const nameA = a.name || a.email || '';
    const nameB = b.name || b.email || '';
    return nameA.localeCompare(nameB);
  });
}, [users]);


// --- Render Logic ---

if (isLoadingAuth || completingSignIn) {
  const message = completingSignIn ? "Signing you in…" : "Verifying access…";
  return <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '50px', textAlign: 'center', fontFamily: 'var(--font-content)', fontWeight: 600, fontSize: '1.2em', color: 'var(--mb-ink)' }}>{message}</div>;
}


// --- Login Screen ---
if (!user) {
  const linkSent = !!signInLinkSentTo;
  return (
    <div className="lp-login">
      <div className="lp-login__card">
        <div className="win95-titlebar" style={{ cursor: 'default' }}>
          <OSIcon32 kind="portal" size={15} className="win95-titlebar__icon" />
          <span className="win95-titlebar__label">LP portal — sign in</span>
        </div>
        <div className="lp-login__card-body">
        <span className="lp-login__eyebrow">Limited Partner access</span>

        {linkSent ? (
          <>
            <h1 className="lp-login__title">Check <em>your email.</em></h1>
            <p className="lp-login__lede">
              We sent a one-tap sign-in link to <strong style={{ color: 'var(--mb-ink)' }}>{signInLinkSentTo}</strong>.
              Open it on this device to sign in. The link expires in about an hour.
            </p>
            {authError && (
              <div role="alert" className="lp-login__error">
                <strong>Error:</strong><span>{authError}</span>
              </div>
            )}
            <div className="lp-login__sent">
              <div className="lp-login__sent-icon" aria-hidden="true" style={{ display: 'flex', justifyContent: 'center' }}>
                <OSIcon32 kind="donate" size={28} />
              </div>
              <button
                type="button"
                onClick={() => { setSignInLinkSentTo(""); setAuthError(""); }}
                className="lp-login__use-different"
              >
                Use a different email
              </button>
            </div>
          </>
        ) : (
          <>
            <h1 className="lp-login__title">Welcome, <em>neighbor.</em></h1>
            <p className="lp-login__lede">
              Sign in to review pitches, vote on grants, and help your chapter decide who gets $1,000 next.
            </p>

            {authError && (
              <div role="alert" className="lp-login__error">
                <strong>Error:</strong><span>{authError}</span>
              </div>
            )}

            <form className="lp-login__form" onSubmit={(e) => { e.preventDefault(); if (!signInLinkSending) handleSendSignInLink(); }}>
              <label className="lp-login__label" htmlFor="lp-login-email">Email address</label>
              <input
                id="lp-login-email"
                className="lp-login__input"
                type="email"
                inputMode="email"
                autoComplete="email"
                placeholder="you@goodneighbor.fund"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
              <button
                type="submit"
                className="lp-login__submit"
                disabled={signInLinkSending}
              >
                {signInLinkSending ? 'Sending…' : 'Email me a sign-in link'}
              </button>
            </form>

            <div className="lp-login__divider" aria-hidden="true">or</div>

            <button
              type="button"
              className="lp-login__google"
              onClick={handleGoogleLogin}
            >
              <svg viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.874 2.684-6.615z"/>
                <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"/>
                <path fill="#FBBC05" d="M3.964 10.706A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.706V4.962H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.038l3.007-2.332z"/>
                <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.962L3.964 7.294C4.672 5.167 6.656 3.58 9 3.58z"/>
              </svg>
              Continue with Google
            </button>
          </>
        )}

        <div className="lp-login__footer">
          <p>This tool is for internal GNF Limited Partner use and requires an admin invite.</p>
          <p>
            Interested in becoming a Limited Partner?{' '}
            <a href="/lp-application" className="lp-login__apply">Apply here →</a>
          </p>
        </div>
        </div>
      </div>
    </div>
  );
}

// Removed invite code screen - no longer needed

// --- Logged-In View ---
return (
  <div className="win95-window" style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden', fontFamily: 'var(--font-content)', boxSizing: 'border-box' }}>
    
    {/* Mobile Styles */}
    <style>{`
      @media (max-width: 768px) {
        .mobile-menubar {
          display: flex !important;
          align-items: center;
          gap: 10px;
        }
        .mobile-menubar__section {
          font-family: var(--font-chrome);
          font-size: 12px;
          font-weight: 700;
          color: var(--chrome-outline);
          min-width: 0;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        /* Drawer: the quarter/membership/community boxes follow the nav
           directly instead of pinning to the bottom of the (tall) drawer. */
        .mobile-sidebar .win95-portal-foot {
          margin-top: 0;
        }
        .mobile-close-btn { display: block !important; }
        .desktop-sidebar { display: none !important; }
        .mobile-sidebar-overlay {
          display: ${isMobileMenuOpen ? 'block' : 'none'} !important;
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          z-index: 998;
        }
        .mobile-sidebar {
          display: flex !important;
          position: fixed;
          top: 0;
          left: ${isMobileMenuOpen ? '0' : '-100%'};
          height: 100dvh;
          width: 76%;
          max-width: 264px;
          background: var(--gnf-bg-gray);
          transition: left 0.3s ease;
          z-index: 999;
          flex-direction: column;
          border-right: 2px solid var(--gnf-border-pink);
          box-shadow: 2px 2px 0 var(--gnf-pink-300);
        }
        .main-content-area {
          width: 100% !important;
        }
        .content-section {
          width: 100% !important;
          max-width: 100% !important;
          padding: 15px !important;
        }
        .stats-grid {
          display: flex !important;
          flex-direction: column !important;
          gap: 15px !important;
        }
        .stats-card {
          width: 100% !important;
        }
        .pitch-card, .member-card {
          width: 100% !important;
          max-width: 100% !important;
        }
        /* Filter bar: 2-col grid with full-width search + pitch-type and
           paired chapter/quarter + Hide/Export. Overrides .filter-bar--one-row's
           nowrap so nothing clips at 390px. Layout:
             row 1: [search       ] (span 2)
             row 2: [chapter][quarter]
             row 3: [pitch-type   ] (span 2)
             row 4: [hide-passes][export]
        */
        .filter-bar, .filter-bar--one-row {
          display: grid !important;
          grid-template-columns: 1fr 1fr !important;
          flex-wrap: wrap !important;
          gap: 6px !important;
          padding: 8px !important;
          margin-bottom: 10px !important;
        }
        .filter-bar select,
        .filter-bar input:not([type="checkbox"]):not([type="radio"]),
        .filter-bar--one-row select,
        .filter-bar--one-row input:not([type="checkbox"]):not([type="radio"]) {
          width: 100% !important;
          min-width: 0 !important;
          min-height: 36px !important;
          font-size: 16px !important; /* 16px floor — smaller triggers iOS zoom-on-focus */
        }
        .filter-bar > input[type="text"],
        .filter-bar > input[type="search"],
        .filter-bar--one-row > input[type="text"],
        .filter-bar--one-row > input[type="search"] {
          grid-column: 1 / -1 !important;
          min-width: 0 !important;
        }
        .filter-bar--one-row > .multi-select-dropdown {
          min-width: 0 !important;
        }
        .filter-bar--one-row > button,
        .filter-bar--one-row > .retro-toggle {
          height: 36px !important;
          min-height: 36px !important;
          padding: 0 10px !important;
          font-size: 12px !important;
        }
        /* Trailing result count sits on its own right-aligned line. */
        .filter-bar > span:last-child,
        .filter-bar--one-row > span:last-child {
          grid-column: 1 / -1 !important;
          text-align: right !important;
        }
        .badge-grid {
          grid-template-columns: repeat(2, 1fr) !important;
          gap: 10px !important;
        }
        .social-cards-container {
          flex-direction: column !important;
          align-items: center !important;
        }
        .social-card-wrapper {
          width: 100% !important;
          max-width: 350px !important;
        }
        .header-text {
          max-width: 200px !important;
          overflow: hidden !important;
          text-overflow: ellipsis !important;
        }
        .logout-btn {
          padding: 6px 10px !important;
          font-size: 12px !important;
        }
        canvas {
          max-width: 100% !important;
          height: auto !important;
        }
        .pitch-card {
          margin-bottom: 10px !important;
        }
        .hide-on-mobile {
          display: none !important;
        }
        
        /* Hide the full stats bar (LP stats / chapter stats / awarded /
           badges) on mobile — it takes too much vertical space. LPs can still
           see these on desktop. */
        .stats-bar {
          display: none !important;
        }
        
        /* Admin table responsive */
        table {
          font-size: 12px !important;
        }
        table th, table td {
          padding: 6px !important;
          font-size: 11px !important;
        }

        /* ── User accounts table — reclaim horizontal room for the member name.
           Drop the row avatar thumbnail and collapse the Edit/Close button to a
           bare icon, and tighten the actions column. The Profile column is hidden
           via .hide-on-mobile (its data is still editable in the expand panel). */
        .member-row-avatar {
          display: none !important;
        }
        .user-action-btn {
          padding: 6px 9px !important;
          min-width: 0 !important;
        }
        .user-action-btn__label {
          display: none !important;
        }
        .user-action-btn__icon {
          display: inline-flex !important;
          vertical-align: middle !important;
        }
        .retro-table th.col-actions,
        .retro-table td.col-actions {
          width: 52px !important;
          padding-left: 4px !important;
          padding-right: 4px !important;
          text-align: center !important;
        }
        
        /* Form responsive */
        form {
          padding: 15px !important;
        }
        textarea {
          font-size: 16px !important; /* 16px floor — smaller triggers iOS zoom-on-focus */
        }
        
        /* Member cards grid */
        .member-cards-grid {
          grid-template-columns: 1fr !important;
        }
        
        /* Multi-select dropdown — full-width trigger, and pin the panel to the
           trigger's own box so it can't shoot past the viewport edge (the panel's
           desktop min-width: 200px overflowed the right column on phones). */
        .multi-select-dropdown {
          width: 100% !important;
        }
        .multi-select-panel {
          left: 0 !important;
          right: 0 !important;
          min-width: 0 !important;
          width: 100% !important;
        }
        .multi-select-panel label {
          display: flex !important;
          align-items: center !important;
        }

        /* ── LP Pitch Card — tighter typography, stacked founder line ── */
        .lp-pitch-card {
          padding: 12px 14px !important;
          margin-bottom: 10px !important;
        }
        .lp-pitch-card__head {
          gap: 8px !important;
        }
        .lp-pitch-card__title {
          font-size: 16px !important;
          line-height: 1.2 !important;
        }
        .lp-pitch-card__by {
          display: block !important;
          font-size: 12px !important;
          margin-top: 2px !important;
        }
        .lp-pitch-card__summary {
          font-size: 13px !important;
          line-height: 1.45 !important;
        }
        .lp-pitch-card__summary-body {
          display: -webkit-box !important;
          -webkit-line-clamp: 4 !important;
          -webkit-box-orient: vertical !important;
          overflow: hidden !important;
        }
        .lp-pitch-card__meta {
          font-size: 11px !important;
          margin-top: 8px !important;
        }

        /* ── Pitch Review grid — full-width single column on mobile with
           reduced card padding and unlimited height so form flows naturally. */
        .pr-grid {
          grid-template-columns: 1fr !important;
          gap: 14px !important;
        }
        .pr-card {
          padding: 18px 14px !important;
          max-height: none !important;
        }
        .pr-header__title {
          font-size: 22px !important;
        }
        .pr-identity {
          gap: 4px 14px !important;
          font-size: 13px !important;
        }
        .pr-actions {
          gap: 6px !important;
          margin-bottom: 16px !important;
        }
        .pr-btn {
          padding: 10px 12px !important;
          font-size: 11px !important;
          min-height: 40px !important;
        }
        .pr-ai {
          padding: 12px 14px !important;
          margin-bottom: 18px !important;
        }
        .pr-ai__body {
          font-size: 13px !important;
        }
        .pr-section {
          padding-top: 14px !important;
          margin-top: 14px !important;
        }
        .pr-field {
          margin-bottom: 14px !important;
        }
        .pr-field__value {
          font-size: 13px !important;
        }
        /* Form controls get 44px touch targets */
        .pr-select,
        .pr-textarea {
          font-size: 16px !important; /* 16px floor — smaller triggers iOS zoom-on-focus */
          padding: 11px 12px !important;
        }
        .pr-select {
          height: 44px !important;
        }
        .pr-form__title {
          font-size: 20px !important;
        }
        .pr-form__group--required {
          padding: 12px 14px !important;
        }
        .pr-submit-row {
          flex-wrap: wrap !important;
          gap: 8px !important;
          padding-top: 14px !important;
        }
        .pr-submit-row .pr-btn {
          padding: 12px 12px !important;
          font-size: 12px !important;
        }

      }
      @media (min-width: 769px) {
        .mobile-menubar { display: none !important; }
        .mobile-sidebar-overlay { display: none !important; }
        .mobile-sidebar { display: none !important; }
        /* Desktop shows the text label; the compact icon is mobile-only. */
        .user-action-btn__icon { display: none; }
      }
    `}</style>

    <ToastContainer />
    
    {/* Badge Notification */}
    {showBadgeNotification && (
      <BadgeNotification 
        badge={showBadgeNotification}
        onClose={() => setShowBadgeNotification(null)}
      />
    )}

    {/* Title bar — same anatomy as every other window: app icon, label,
        system buttons. The portal is always maximised, so minimise and
        maximise render disabled; Close leaves for the desktop with no
        windows open. Logout lives in the sidebar user card. */}
    <div className="win95-titlebar" style={{ flexShrink: 0, cursor: 'default' }}>
      <OSIcon32 kind="portal" size={15} className="win95-titlebar__icon" />
      <span className="win95-titlebar__label">
        {(user?.chapter || "NeighborhoodOS") + " — LP portal"}
        <span style={{ fontWeight: 400, opacity: 0.85 }}>{'  ·  '}{user?.name || user?.email}</span>
      </span>
      <div className="win95-titlebar__btns">
        <button
          className="win95-btn-sys"
          type="button"
          disabled
          aria-label="Minimise (not available in the portal)"
          title="The portal is always maximised"
        >
          <span className="win95-btn-sys__min" aria-hidden="true" />
        </button>
        <button
          className="win95-btn-sys"
          type="button"
          disabled
          aria-label="Maximise (not available in the portal)"
          title="The portal is always maximised"
        >
          <span className="win95-btn-sys__max" aria-hidden="true" />
        </button>
        <button
          className="win95-btn-sys win95-close-btn"
          type="button"
          onClick={handleClosePortal}
          aria-label="Close LP portal"
          title="Close the portal and return to the desktop"
        >
          ✕
        </button>
      </div>
    </div>

    {/* Mobile menu row — a Win98 menu strip under the title bar (a title bar
        never hosts app controls). Opens the navigation drawer; names the
        section you're in. Hidden on desktop, where the sidebar is visible. */}
    <div className="mobile-menubar win95-toolbar" style={{ flex: 'none' }}>
      <button
        type="button"
        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        className="win95-btn win95-btn--compact"
        aria-label="Open navigation menu"
        aria-expanded={isMobileMenuOpen}
      >
        ☰ Menu
      </button>
      <span className="mobile-menubar__section">
        {activeTab === 'adminPanel'
          ? 'Admin panel'
          : (PORTAL_NAV_ITEMS.find((i) => i.key === activeTab)?.label || '')}
      </span>
    </div>

    {/* Main Content Area with Sidebar */}
    <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }} className="main-content-container">
      {/* Mobile Sidebar Overlay */}
      <div 
        className="mobile-sidebar-overlay" 
        onClick={() => setIsMobileMenuOpen(false)}
        style={{ display: 'none' }}
      />
      
      {/* Desktop Sidebar */}
      <div className="desktop-sidebar win95-portal-sidebar win95-scroll">
        <NavigationItems />
      </div>

      {/* Mobile Sidebar */}
      <div className="mobile-sidebar win95-portal-sidebar" style={{ display: 'none' }}>
        <NavigationItems />
      </div>

      {/* Main Content Area (Scrollable). The review tab manages its own
          full-bleed layout + scrolling (toolbar/list/status bar); the admin
          panel pins its tab strip and scrolls inside `.admin-tabpanel` so the
          Reviews live view can flex-fill to the bottom edge; tabs with their
          own chrome pane headers render flush; the rest keep the padded
          scroll container. */}
      <div
        ref={listScrollRef}
        style={['reviewPitches', 'adminPanel'].includes(activeTab)
          ? { flex: 1, overflow: 'hidden', padding: 0, background: 'var(--gnf-bg)', display: 'flex', flexDirection: 'column', minWidth: 0 }
          : ['statistics', 'chapterMembers', 'socialCards', 'badges', 'pitchMap', 'resources'].includes(activeTab)
            ? { flex: 1, overflowY: 'auto', padding: 0, background: 'var(--gnf-bg)', minWidth: 0 }
            : { flex: 1, overflowY: 'auto', padding: '20px', background: 'var(--gnf-bg)', minWidth: 0 }}
        className={`main-content-area content-section ${['reviewPitches', 'adminPanel'].includes(activeTab) ? '' : 'win95-scroll'}`}>

      {/* --- Review Pitches Tab Content --- */}
      {activeTab === 'reviewPitches' && (
        <ReviewPitchesTab
          user={user}
          userChapter={userChapter}
          isSuperAdmin={isSuperAdmin}
          lpPitches={lpPitches}
          lpPitchesLoaded={lpPitchesLoaded}
          lpFilteredPitches={lpFilteredPitches}
          selectedPitch={selectedPitch}
          setSelectedPitch={setSelectedPitch}
          reviews={reviews}
          reviewFormData={reviewFormData}
          pitchComments={pitchComments}
          reviewSubmitPhase={reviewSubmitPhase}
          reviewSubmitWasEdit={reviewSubmitWasEdit}
          hidePassedReviews={hidePassedReviews}
          setHidePassedReviews={setHidePassedReviews}
          reviewSearchTerm={reviewSearchTerm}
          setReviewSearchTerm={setReviewSearchTerm}
          reviewFilter={reviewFilter}
          setReviewFilter={setReviewFilter}
          reviewChapterFilter={reviewChapterFilter}
          setReviewChapterFilter={setReviewChapterFilter}
          reviewQuarterFilter={reviewQuarterFilter}
          setReviewQuarterFilter={setReviewQuarterFilter}
          reviewCategoryFilter={reviewCategoryFilter}
          setReviewCategoryFilter={setReviewCategoryFilter}
          getNextUnreviewedPitch={getNextUnreviewedPitch}
          selectPitchForReview={selectPitchForReview}
          handleBackToList={handleBackToList}
          handleReviewSubmit={handleReviewSubmit}
          handleReviewFormChange={handleReviewFormChange}
          handleLpPitchExport={handleLpPitchExport}
          MultiSelectDropdown={MultiSelectDropdown}
          formatDate={formatDate}
          awardedDollars={(() => {
            // Resolved dollar sum: respects per-pitch awardAmount overrides and
            // per-chapter defaults, falling back to the historical $1,000 only
            // for winners that predate both.
            const pitchesToUse = isAdmin ? adminPitches : lpPitches;
            const scopedWinners = (user.chapter
              ? pitchesToUse.filter(p => p.isWinner && p.chapter === user.chapter)
              : pitchesToUse.filter(p => p.isWinner));
            return sumAwarded(scopedWinners, chaptersByNameMap(chapters));
          })()}
          reviewedThisQuarter={userStats?.quarterlyReviews?.[getCurrentQuarter()] || 0}
          onQuickRate={submitQuickRating}
          isMobileViewport={isMobileViewport}
        />
      )}

      {/* --- Social Cards Tab Content --- */}
      {activeTab === 'socialCards' && (
        <SocialCardsTab
          user={user}
          userStats={userStats}
          userBadges={userBadges}
          isAdmin={isAdmin}
          adminPitches={adminPitches}
          lpPitches={lpPitches}
        />
      )}

      {/* --- Admin Panel Tab Content --- */}
      {activeTab === 'adminPanel' && isAdmin && (
        <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
          {/* Admin Sub-Tabs Navigation — Win95 folder-tab look.
              Tab strip extracted to a shared primitive with full keyboard
              (←/→, Home/End) support and proper role="tablist" semantics.
              The panel below is `.admin-tabpanel` which merges visually with
              the selected tab via negative margin. */}
          <AdminTabStrip
            ariaLabel="Admin sections"
            activeKey={activeAdminTab}
            onChange={setActiveAdminTab}
            tabs={[
              { key: 'pitchesAndReviews', label: 'Reviews' },
              { key: 'grantWinners', label: 'Grant awards' },
              // Forms Library responses — chapter directors see their own
              // chapter's, superAdmins see all (Firestore rules enforce).
              ...(isSuperAdmin || isChapterDirector
                ? [{ key: 'formResponses', label: 'Form responses' }]
                : []),
              { key: 'userManagement', label: 'Users' },
              // Chapter directors get the Chapter tab too, but the tab's
              // render branch scopes the list + form to their own chapter.
              ...(isSuperAdmin || isChapterDirector
                ? [{ key: 'chaptersManagement', label: 'Chapter' }]
                : []),
              // Chapter directors get the Resources tab too, scoped to their
              // own chapter via the render branch + Firestore rules.
              ...(isSuperAdmin || isChapterDirector
                ? [{ key: 'resourcesManagement', label: 'Resources' }]
                : []),
              { key: 'createUser', label: 'Create user' },
              ...(isSuperAdmin
                ? [{ key: 'superAdminTools', label: 'Super admin tools' }]
                : []),
            ]}
          />
          {/* Scrolls its own content (tab strip above stays pinned). The
              flex-column display lets the Reviews live view flex-fill the
              full remaining height; every other sub-tab flows naturally and
              scrolls here instead of at the page level. */}
          <div className="admin-tabpanel win95-scroll" style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflowY: 'auto' }}>

          {/* Admin: Pitches & Reviews Sub-Tab Content */}
          {activeAdminTab === 'pitchesAndReviews' && (
            <ReviewsAdminTab
              user={user}
              users={users}
              chapters={chapters}
              isAdmin={isAdmin}
              isSuperAdmin={isSuperAdmin}
              userChapter={userChapter}
              reviews={reviews}
              adminPitches={adminPitches}
              setAdminPitches={setAdminPitches}
              allReviewsData={allReviewsData}
              adminNotes={adminNotes}
              setAdminNotes={setAdminNotes}
              adminNoteDrafts={adminNoteDrafts}
              setAdminNoteDrafts={setAdminNoteDrafts}
              editingAdminNoteId={editingAdminNoteId}
              setEditingAdminNoteId={setEditingAdminNoteId}
              editingAdminNoteText={editingAdminNoteText}
              setEditingAdminNoteText={setEditingAdminNoteText}
              adminNoteSavingId={adminNoteSavingId}
              setAdminNoteSavingId={setAdminNoteSavingId}
              shortlistTogglingId={shortlistTogglingId}
              setShortlistTogglingId={setShortlistTogglingId}
              adminChapterFilter={adminChapterFilter}
              setAdminChapterFilter={setAdminChapterFilter}
              adminQuarterFilter={adminQuarterFilter}
              setAdminQuarterFilter={setAdminQuarterFilter}
              adminSearch={adminSearch}
              setAdminSearch={setAdminSearch}
              adminHidePassed={adminHidePassed}
              setAdminHidePassed={setAdminHidePassed}
              adminFavoriteFilterMode={adminFavoriteFilterMode}
              setAdminFavoriteFilterMode={setAdminFavoriteFilterMode}
              adminSortMode={adminSortMode}
              setAdminSortMode={setAdminSortMode}
              expandedPitchId={expandedPitchId}
              setExpandedPitchId={setExpandedPitchId}
              focusedPitchId={focusedPitchId}
              setFocusedPitchId={setFocusedPitchId}
              detailsDrawerOpen={detailsDrawerOpen}
              setDetailsDrawerOpen={setDetailsDrawerOpen}
              loadAdminData={loadAdminData}
              loadLPData={loadLPData}
              MultiSelectDropdown={MultiSelectDropdown}
              formatDate={formatDate}
              isMobile={isMobile}
              showAppAlert={showAppAlert}
              showAppConfirm={showAppConfirm}
            />
          )}

          {activeAdminTab === 'grantWinners' && (
            <GrantWinnersAdminTab
              user={user}
              isChapterDirector={isChapterDirector}
              activeChapterNames={activeChapterNames}
              adminPitches={adminPitches}
              chapters={chapters}
              pendingChanges={pendingChanges}
              setPendingChanges={setPendingChanges}
              aboutById={aboutById}
              setAboutById={setAboutById}
              websiteById={websiteById}
              setWebsiteById={setWebsiteById}
              photosById={photosById}
              setPhotosById={setPhotosById}
              generatingAboutId={generatingAboutId}
              setGeneratingAboutId={setGeneratingAboutId}
              uploadingPhotoFor={uploadingPhotoFor}
              setUploadingPhotoFor={setUploadingPhotoFor}
              winnerChapterFilter={winnerChapterFilter}
              setWinnerChapterFilter={setWinnerChapterFilter}
              winnerSearchTerm={winnerSearchTerm}
              setWinnerSearchTerm={setWinnerSearchTerm}
              loadAdminData={loadAdminData}
              showAppAlert={showAppAlert}
            />
          )}

          {/* Admin: Form Responses Sub-Tab Content — completed Forms Library
              submissions (LP onboarding, microgrant awardee). Lives here so
              directors find responses in one place instead of digging through
              per-form tabs in Resources. */}
          {activeAdminTab === 'formResponses' && (isSuperAdmin || isChapterDirector) && (
            <FormResponsesAdminTab user={user} isSuperAdmin={isSuperAdmin} />
          )}

          {/* Admin: User Management Sub-Tab Content */}
          {activeAdminTab === 'userManagement' && (
            <UsersAdminTab
              user={user}
              users={users}
              sortedUsers={sortedUsers}
              chapters={chapters}
              activeChapterNames={activeChapterNames}
              isAdmin={isAdmin}
              isSuperAdmin={isSuperAdmin}
              isChapterDirector={isChapterDirector}
              isLP={isLP}
              userChapter={userChapter}
              editingUsers={editingUsers}
              setEditingUsers={setEditingUsers}
              uploadingPhotoFor={uploadingPhotoFor}
              setUploadingPhotoFor={setUploadingPhotoFor}
              updatingEmailFor={updatingEmailFor}
              setUpdatingEmailFor={setUpdatingEmailFor}
              loadAdminData={loadAdminData}
              loadChapterMembers={loadChapterMembers}
              requestConfirm={requestConfirm}
              showAppAlert={showAppAlert}
              showAppConfirm={showAppConfirm}
              sendSignInLinkCallable={sendSignInLinkCallable}
            />
          )}

          {/* Admin: Create User Sub-Tab Content */}
          {activeAdminTab === 'createUser' && isAdmin && (
            <CreateUserAdminTab
              isAdmin={isAdmin}
              newUserData={newUserData}
              setNewUserData={setNewUserData}
              isInviting={isInviting}
              setIsInviting={setIsInviting}
              isSuperAdmin={isSuperAdmin}
              isChapterDirector={isChapterDirector}
              userChapter={userChapter}
              activeChapterNames={activeChapterNames}
              loadAdminData={loadAdminData}
              showAppAlert={showAppAlert}
            />
          )}

          {/* Admin: Super Admin Tools Sub-Tab Content (Conditional) */}
          {activeAdminTab === 'superAdminTools' && isSuperAdmin && (
            <SuperAdminToolsTab
              users={users}
              isAdmin={isAdmin}
              isSuperAdmin={isSuperAdmin}
              loadAdminData={loadAdminData}
              showAppAlert={showAppAlert}
              showAppConfirm={showAppConfirm}
            />
          )}

          {/* Chapters Management Sub-Tab Content */}
          {activeAdminTab === 'chaptersManagement' && (isSuperAdmin || isChapterDirector) && (
            <ChapterAdminTab
              user={user}
              users={users}
              chapters={chapters}
              isSuperAdmin={isSuperAdmin}
              isChapterDirector={isChapterDirector}
              userChapter={userChapter}
              editingChapterId={editingChapterId}
              setEditingChapterId={setEditingChapterId}
              chapterFormData={chapterFormData}
              setChapterFormData={setChapterFormData}
              chapterFormTab={chapterFormTab}
              setChapterFormTab={setChapterFormTab}
              isAddingChapter={isAddingChapter}
              setIsAddingChapter={setIsAddingChapter}
              showNewChapterWizard={showNewChapterWizard}
              setShowNewChapterWizard={setShowNewChapterWizard}
              uploadingPhotoFor={uploadingPhotoFor}
              setUploadingPhotoFor={setUploadingPhotoFor}
              loadChapters={loadChapters}
              requestConfirm={requestConfirm}
              showAppAlert={showAppAlert}
              showAppConfirm={showAppConfirm}
            />
          )}

          {/* Resources Management Sub-Tab Content */}
          {activeAdminTab === 'resourcesManagement' && (isSuperAdmin || isChapterDirector) && (
            <ResourcesAdminTab
              user={user}
              userChapter={userChapter}
              isChapterDirector={isChapterDirector}
              activeChapterNames={activeChapterNames}
              managedResources={managedResources}
              editingResource={editingResource}
              setEditingResource={setEditingResource}
              resourceFormData={resourceFormData}
              setResourceFormData={setResourceFormData}
              isAddingResource={isAddingResource}
              setIsAddingResource={setIsAddingResource}
              resourceSearchTerm={resourceSearchTerm}
              setResourceSearchTerm={setResourceSearchTerm}
              resourceTypeFilter={resourceTypeFilter}
              setResourceTypeFilter={setResourceTypeFilter}
              resourceStageFilter={resourceStageFilter}
              setResourceStageFilter={setResourceStageFilter}
              resourceChapterFilter={resourceChapterFilter}
              setResourceChapterFilter={setResourceChapterFilter}
              resourceCsvImport={resourceCsvImport}
              setResourceCsvImport={setResourceCsvImport}
              resourceCsvFileInputRef={resourceCsvFileInputRef}
              loadManagedResources={loadManagedResources}
              requestConfirm={requestConfirm}
              showAppAlert={showAppAlert}
            />
          )}
          </div> {/* End .admin-tabpanel */}
        </div>
      )} {/* End Admin Panel Content */}
      
      {/* --- Badges Tab Content --- */}
      {activeTab === 'badges' && (
        <TrophyCase
          badges={userBadges || []}
          userStats={userStats || {}}
        />
      )}

      {/* --- Statistics Tab Content --- */}
      {/* Tab renders its own chrome pane header — no hero band needed */}
      {activeTab === 'statistics' && (
        <StatisticsTab user={user} chaptersFromPortal={chapters} />
      )}

      {/* --- Chapter Members Tab Content --- */}
      {activeTab === 'chapterMembers' && (
        <ChapterMembersTab chapterMembers={chapterMembers} />
      )}

      {/* --- Pitch Map Tab Content --- */}
      {activeTab === 'pitchMap' && (
        <div style={{ height: 'max(420px, calc(100dvh - 160px))', display: 'flex', flexDirection: 'column' }}>
          <PitchMap
            // Manually logged grant awards aren't applications; the pitch map
            // charts where applications come from, so they're excluded. The
            // public founder map still shows them once published.
            pitches={(isAdmin ? adminPitches : lpPitches).filter((p) => !isManualAward(p))}
            currentUserChapter={user?.chapter}
          />
        </div>
      )}

      {/* --- Resources Tab Content --- */}
      {activeTab === 'resources' && (
        <div>
          <ResourceLibrary />
          {(isChapterDirector || isSuperAdmin) && (
            <>
              <FormsLibrary user={user} isSuperAdmin={isSuperAdmin} />
              <AssetsLibrary />
            </>
          )}
        </div>
      )}

      {/* --- Bulletin Board Tab Content --- */}
      {activeTab === 'bulletinBoard' && (
        <BulletinBoardTab
          user={user}
          isAdmin={isAdmin}
          chapterMembers={chapterMembers}
          bulletinMessages={bulletinMessages}
          newBulletinMessage={newBulletinMessage}
          setNewBulletinMessage={setNewBulletinMessage}
          lastViewedTimestamp={lastViewedTimestamp}
          loadBulletinMessages={loadBulletinMessages}
        />
      )}
      
      </div> {/* End Main Content Area */}
    </div> {/* End Main Content Container */}
    {confirmDialog /* Retro confirm modal for destructive admin actions */}
  </div> // End Logged-In Container
); // End Main Return

} // End Component